import { useState } from 'react'
import PropTypes from 'prop-types'
import { Badge } from '@context365/badge'
import { Button, IconButton } from '@context365/button'
import { Input, Select } from '@context365/forms'
import {
  Add,
  CloudUploadOutlined,
  DeleteOutline,
  Edit,
} from '@context365/icons'
import { Divider, Upload } from 'antd'
import produce from 'immer'
import { join, last, split } from 'lodash'
import find from 'lodash/find'
import isNil from 'lodash/isNil'
import map from 'lodash/map'
import { useSelector } from 'react-redux'
import { BASE_URL, REACT_APP_STORAGE_URL } from '../../../config'
import { getFileExtension } from '../../../utils/helpers'
import ArrayItemHeading from './ArrayItemHeading'

const generateEmptyMedia = () => ({
  mediaCategoryId: null,
  title: null,
  url: null,
  isEdit: true,
})

const { Dragger } = Upload

const AcceptedExtensions = ['.avi', '.mov', '.mp3', '.png', '.jpg']

const isCorrectFileExtension = (file) => {
  const extension = getFileExtension(file.name)
  return AcceptedExtensions.includes(`.${extension.toLowerCase()}`)
}

const EditMedia = ({
  heading,
  title,
  url,
  mediaCategoryId,
  mediaCategories,
  onFieldChange,
  onSave,
  onRemove,
}) => {
  const [allowUrlInput, setAllowUrlInput] = useState(
    url && !url.startsWith(REACT_APP_STORAGE_URL)
  )

  const accessToken = useSelector((state) => state.auth.accessToken)
  const [file, setFile] = useState(
    url
      ? {
          url,
          name: last(split(decodeURI(url), '/')),
          status: 'done',
          uid: 1,
        }
      : null
  )

  const handleUpload = (info) => {
    const { status, response } = info.file

    if (status === 'removed') {
      setFile(null)
      onFieldChange('url', null)
      return
    }

    const isIncludedInAccept = isCorrectFileExtension(info.file)

    if (!isIncludedInAccept) {
      info.file.status = 'error'
      info.file.response = 'Incorrect file type'
      return
    }

    setFile(info.file)
    if (status === 'done' && !isNil(response)) {
      const { url } = response
      onFieldChange('url', encodeURI(url))
    }
  }

  return (
    <>
      <ArrayItemHeading heading={heading} onRemove={onRemove} />
      <div className="grid grid-cols-2 gap-6 py-4">
        <Input
          label="Media title"
          value={title}
          onChange={(e) => onFieldChange('title', e.target.value)}
        />
        <Select
          label="Media category"
          clearable
          required
          value={mediaCategoryId}
          onChange={(value) => onFieldChange('mediaCategoryId', value)}
        >
          {map(mediaCategories, (dc) => (
            <Select.Option key={dc.id} value={dc.id}>
              {dc.name}
            </Select.Option>
          ))}
        </Select>
        {!allowUrlInput && (
          <div>
            <Dragger
              className="col-span-2"
              accept={join(AcceptedExtensions, ',')}
              action={`${BASE_URL}uploads`}
              headers={{ Authorization: `Bearer ${accessToken}` }}
              onChange={handleUpload}
              fileList={file ? [{ ...file }] : []}
            >
              <CloudUploadOutlined size="28px" className="text-grey-400" />
              <div className="type-header-normal-extra-small text-primary-5">
                Drag or click here to upload a document
              </div>
              <div className="type-header-normal-extra-small text-primary-5">
                (AVI, MOV, MP3, PNG, JPG / Max file size 100mb)
              </div>
            </Dragger>
            <span>
              Want to link to a Vimeo or YouTube video instead?
              <Button variant="link" onClick={() => setAllowUrlInput(true)}>
                Click here.
              </Button>
            </span>
          </div>
        )}
        {allowUrlInput && (
          <div className="col-start-1">
            <Input
              label="Youtube or Vimeo link"
              value={url}
              onChange={(e) => onFieldChange('url', e.target.value)}
            />
            <span>
              Want to upload a movie or image file instead?
              <Button variant="link" onClick={() => setAllowUrlInput(false)}>
                Click here.
              </Button>
            </span>
          </div>
        )}
        <div className="col-span-2 mt-12">
          <Button variant="filled" onClick={() => onSave()}>
            Close Media Editor
          </Button>
        </div>
      </div>
    </>
  )
}

const ViewMedia = ({ title, category, onRemove, onEdit }) => (
  <div className="space-x-4">
    <IconButton
      icon={<DeleteOutline className="text-red-110" />}
      label="delete"
      variant="link"
      onClick={() => onRemove()}
    />
    <IconButton
      icon={<Edit className="text-primary-110" />}
      label="edit"
      variant="link"
      onClick={() => onEdit()}
    />
    <span>{title ?? 'No title'}</span>
    {category && <Badge>{category}</Badge>}
  </div>
)

const Media = ({
  media,
  updateMedia,
  mediaCategories,
  showEmptyEdit = true,
}) => {
  const handleAddMedia = () => {
    const newMedia = produce(media, (newState) => {
      if (newState.length === 0 && showEmptyEdit) {
        newState.push(generateEmptyMedia())
      }
      newState.push(generateEmptyMedia())
    })
    updateMedia(newMedia)
  }

  const handleRemoveMedia = (index) => {
    const newMedia = produce(media, (newState) => {
      newState.splice(index, 1)
    })
    updateMedia(newMedia)
  }

  const handleEditMedia = (index, key, value) => {
    const newMedia = produce(media, (newState) => {
      const currentItem = newState[index]
      if (currentItem) {
        currentItem[key] = value
      } else {
        newState.push({ ...generateEmptyMedia(), [key]: value })
      }
    })
    updateMedia(newMedia)
  }

  const hasNoItems = media.length === 0
  const mediaIteratees =
    hasNoItems && showEmptyEdit ? [generateEmptyMedia()] : media

  return (
    <div>
      {map(mediaIteratees, (media, index) => {
        const category = find(
          mediaCategories,
          (mc) => mc.id === media.mediaCategoryId
        )

        return (
          <div className="py-2">
            {media.isEdit ? (
              <EditMedia
                {...media}
                heading={`Media ${index + 1}`}
                mediaCategories={mediaCategories}
                onFieldChange={(key, value) =>
                  handleEditMedia(index, key, value)
                }
                onSave={() => handleEditMedia(index, 'isEdit', false)}
                onRemove={hasNoItems ? null : () => handleRemoveMedia(index)}
              />
            ) : (
              <ViewMedia
                title={media.title}
                category={category?.name}
                onEdit={() => handleEditMedia(index, 'isEdit', true)}
                onRemove={() => handleRemoveMedia(index)}
              />
            )}
          </div>
        )
      })}
      <Divider className="col-span-2" style={{ marginTop: 40 }} />
      <Button variant="link" size="small" onClick={() => handleAddMedia()}>
        <Add size="small" /> Add Media
      </Button>
    </div>
  )
}

Media.propTypes = {
  media: PropTypes.array.isRequired,
  updateMedia: PropTypes.func.isRequired,
  mediaCategories: PropTypes.array.isRequired,
}

export default Media
